import { useState } from "react";
import { toast } from "sonner";
import { CalendarCheck, Lock, MessageCircle, ShieldCheck } from "lucide-react";
import { buildAppointmentMessage, PHONE_INTL } from "@/lib/whatsapp";

const SERVICES = [
  "Ultrasound / Sonography",
  "Pregnancy Sonography",
  "3D / 4D Scan",
  "Colour Doppler",
  "Digital X-Ray",
  "Dental OPG X-Ray",
  "Thyroid / Abdomen / Pelvis Scan",
  "Obstetric Sonography",
  "Other / Not Sure",
];

export default function AppointmentForm() {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    service: "",
    date: "",
    time: "",
    message: "",
  });

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim() || !form.service) {
      toast.error("Please fill your name, phone and select a service.");
      return;
    }
    const phoneClean = form.phone.replace(/\D/g, "");
    if (phoneClean.length < 10) {
      toast.error("Please enter a valid 10-digit phone number.");
      return;
    }
    const text = buildAppointmentMessage(form);
    const url = `https://wa.me/${PHONE_INTL}?text=${encodeURIComponent(text)}`;
    toast.success("Opening WhatsApp with your details…");
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <section
      id="appointment"
      data-testid="appointment-section"
      className="py-20 lg:py-28 bg-white relative overflow-hidden"
    >
      <div className="absolute -top-24 -right-24 w-72 h-72 rounded-full bg-[#00BFFF]/10 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-[#0099CC]/10 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-12 items-start">
          <div className="lg:col-span-5">
            <span className="text-xs font-semibold tracking-[0.2em] uppercase text-[#0099CC]">
              Book Appointment
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900">
              Reserve Your Slot in <span className="text-[#0099CC]">Under a Minute</span>
            </h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
              Fill the form — we’ll open WhatsApp with your details pre-filled. Send the
              message and our team will confirm your appointment quickly.
            </p>

            <ul className="mt-8 space-y-3">
              <li className="flex items-start gap-3">
                <span className="mt-0.5 w-9 h-9 rounded-xl bg-[#25D366]/10 text-[#1ebd5a] flex items-center justify-center shrink-0">
                  <MessageCircle className="w-5 h-5" />
                </span>
                <div>
                  <p className="font-semibold text-slate-900">Direct confirmation over WhatsApp</p>
                  <p className="text-sm text-slate-600">Our team replies on the same chat — fast and personal.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 w-9 h-9 rounded-xl bg-[#00BFFF]/10 text-[#0099CC] flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-5 h-5" />
                </span>
                <div>
                  <p className="font-semibold text-slate-900">No sign-up required</p>
                  <p className="text-sm text-slate-600">No accounts, no OTPs — just a simple booking flow.</p>
                </div>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 w-9 h-9 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center shrink-0">
                  <Lock className="w-5 h-5" />
                </span>
                <div>
                  <p className="font-semibold text-slate-900">Your details stay private</p>
                  <p className="text-sm text-slate-600">We don’t store your form data on the website.</p>
                </div>
              </li>
            </ul>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={handleSubmit}
              data-testid="appointment-form"
              className="bg-white rounded-3xl border border-slate-100 shadow-[0_30px_60px_rgba(15,23,42,0.08)] p-6 sm:p-8 lg:p-10"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5" htmlFor="ap-name">
                    Full Name <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="ap-name"
                    type="text"
                    required
                    value={form.name}
                    onChange={update("name")}
                    placeholder="e.g. Pratima Kadam"
                    data-testid="form-name"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00BFFF]/40 focus:border-[#00BFFF] transition-all text-slate-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5" htmlFor="ap-phone">
                    Phone Number <span className="text-rose-500">*</span>
                  </label>
                  <input
                    id="ap-phone"
                    type="tel"
                    required
                    inputMode="numeric"
                    value={form.phone}
                    onChange={update("phone")}
                    placeholder="10-digit mobile number"
                    data-testid="form-phone"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00BFFF]/40 focus:border-[#00BFFF] transition-all text-slate-800"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5" htmlFor="ap-service">
                    Service Required <span className="text-rose-500">*</span>
                  </label>
                  <select
                    id="ap-service"
                    required
                    value={form.service}
                    onChange={update("service")}
                    data-testid="form-service"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00BFFF]/40 focus:border-[#00BFFF] transition-all text-slate-800"
                  >
                    <option value="">Select a service…</option>
                    {SERVICES.map((s) => (
                      <option key={s} value={s}>{s}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5" htmlFor="ap-date">
                    Preferred Date
                  </label>
                  <input
                    id="ap-date"
                    type="date"
                    value={form.date}
                    onChange={update("date")}
                    data-testid="form-date"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00BFFF]/40 focus:border-[#00BFFF] transition-all text-slate-800"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5" htmlFor="ap-time">
                    Preferred Time
                  </label>
                  <input
                    id="ap-time"
                    type="time"
                    value={form.time}
                    onChange={update("time")}
                    data-testid="form-time"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00BFFF]/40 focus:border-[#00BFFF] transition-all text-slate-800"
                  />
                </div>

                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1.5" htmlFor="ap-message">
                    Message (optional)
                  </label>
                  <textarea
                    id="ap-message"
                    rows={3}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Anything we should know before your visit?"
                    data-testid="form-message"
                    className="w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#00BFFF]/40 focus:border-[#00BFFF] transition-all text-slate-800 resize-none"
                  />
                </div>
              </div>

              <button
                type="submit"
                data-testid="form-submit-btn"
                className="mt-7 w-full inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1ebd5a] text-white font-semibold py-3.5 px-6 rounded-full shadow-lg hover:shadow-xl hover:-translate-y-0.5 transition-all"
              >
                <CalendarCheck className="w-5 h-5" />
                Send via WhatsApp
              </button>

              <p className="mt-3 text-center text-xs text-slate-500">
                By submitting, you’ll be redirected to WhatsApp with your details pre-filled.
              </p>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
